import React from 'react';
import { Box } from '@mui/material';
import type { useScene } from 'src/hooks/useScene';
import { getTilePosition } from 'src/utils';
import { getAnchorTile } from 'src/core/connections/connectionUtils';
import { normalizeConnectorAnchors } from 'src/core/connections/waypointSimplification';

interface Props {
  connector: ReturnType<typeof useScene>['connectors'][number];
  view: ReturnType<typeof useScene>['currentView'];
  activeAnchorId: string | null;
}

export const ConnectorWaypoints: React.FC<Props> = ({
  connector,
  view,
  activeAnchorId
}) => {
  const visibleAnchors = activeAnchorId
    ? connector.anchors
    : normalizeConnectorAnchors(connector.anchors, view);

  return (
    <>
      {visibleAnchors.map((anchor, index) => {
        try {
          const tile = getAnchorTile(anchor, view);
          const position = getTilePosition({ tile, origin: 'CENTER' });
          const isActive = activeAnchorId === anchor.id;
          const isAnotherAnchorActive = activeAnchorId !== null && !isActive;
          const isEndpoint = index === 0 || index === visibleAnchors.length - 1;

          return (
            <Box
            key={anchor.id}
            data-scene-item-type="CONNECTOR_ANCHOR"
            data-scene-item-id={anchor.id}
            data-connector-id={connector.id}
            aria-label={isEndpoint ? 'Connector endpoint' : 'Connector waypoint'}
            aria-pressed={isActive}
            sx={{
              position: 'absolute',
              left: position.x,
              top: position.y,
              // Interaction target is intentionally larger than the visible
              // dot so the adjacent connector stroke cannot steal the press.
              width: 28,
              height: 28,
              transform: 'translate(-50%, -50%)',
              borderRadius: '50%',
              bgcolor: 'transparent',
              cursor: isActive ? 'grabbing' : 'grab',
              pointerEvents: isAnotherAnchorActive ? 'none' : 'auto',
              zIndex: 1000,
              opacity: isAnotherAnchorActive ? 0.55 : 1,
              '&::before': {
                content: '""',
                position: 'absolute',
                left: '50%',
                top: '50%',
                width: isActive ? 16 : 14,
                height: isActive ? 16 : 14,
                transform: 'translate(-50%, -50%)',
                borderRadius: '50%',
                bgcolor: isActive ? '#1d4ed8' : '#3b82f6',
                border: '2px solid white',
                boxShadow: '0 1px 5px rgba(15, 23, 42, 0.4)',
                transition: 'transform 120ms ease, background-color 120ms ease'
              },
              '&:hover::before': {
                bgcolor: isActive ? '#1d4ed8' : '#2563eb',
                transform: 'translate(-50%, -50%) scale(1.2)'
              },
              '&:active': {
                cursor: 'grabbing'
              },
              '&:active::before': {
                bgcolor: '#1d4ed8'
              }
            }}
            />
          );
        } catch {
          return null;
        }
      })}
    </>
  );
};
